import type { TrainingDay, TrainingExerciseEntry, TrainingPlanPayload, TrainingSetTarget } from '../types';

/** Legacy flat list stored as JSON array (v1 builder) */
export interface LegacyWorkoutExerciseEntry {
  exerciseId?: string;
  name?: string;
  sets: number;
  reps: number;
  weight: number;
}

export function newId(): string {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `id-${Math.random().toString(36).slice(2, 11)}-${Date.now().toString(36)}`;
}

function coerceSetRow(raw: unknown): TrainingSetTarget | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  return {
    setId: typeof r.setId === 'string' && r.setId.length > 0 ? r.setId : newId(),
    reps: Math.max(1, Math.floor(Number(r.reps)) || 10),
    weightKg: Math.max(0, Number(r.weightKg) || 0),
  };
}

function setTargetsFromLegacyCounts(
  setsCount: number,
  reps: number,
  weightKg: number
): TrainingSetTarget[] {
  const n = Math.max(1, setsCount);
  const r = Math.max(1, reps);
  const w = Math.max(0, weightKg);
  return Array.from({ length: n }, () => ({ setId: newId(), reps: r, weightKg: w }));
}

function ensureStrengthSetTargets(o: Record<string, unknown>): TrainingSetTarget[] {
  const st = o.setTargets;
  if (Array.isArray(st) && st.length > 0) {
    const rows = st.map(coerceSetRow).filter((x): x is TrainingSetTarget => x != null);
    if (rows.length > 0) return rows;
  }
  const sets = Math.max(1, Math.floor(Number(o.sets)) || 3);
  const reps = Math.max(1, Math.floor(Number(o.reps)) || 10);
  const weightKg = Math.max(0, Number(o.weightKg ?? o.weight) || 0);
  return setTargetsFromLegacyCounts(sets, reps, weightKg);
}

function legacyToExercise(e: LegacyWorkoutExerciseEntry): TrainingExerciseEntry {
  const w = Number(e.weight) || 0;
  const sets = Math.max(1, Math.floor(Number(e.sets)) || 3);
  const reps = Math.max(1, Math.floor(Number(e.reps)) || 10);
  return {
    entryId: newId(),
    kind: 'strength',
    name: typeof e.name === 'string' && e.name.trim() ? e.name.trim() : 'Exercise',
    setTargets: setTargetsFromLegacyCounts(sets, reps, w),
  };
}

function coerceExercise(raw: unknown): TrainingExerciseEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const entryId = typeof o.entryId === 'string' && o.entryId.length > 0 ? o.entryId : newId();
  const kind = o.kind === 'cardio' ? 'cardio' : 'strength';
  const name = typeof o.name === 'string' && o.name.trim() ? o.name.trim() : 'Exercise';
  if (kind === 'cardio') {
    const targetMinutes = Math.max(1, Math.floor(Number(o.targetMinutes)) || 10);
    return { entryId, kind: 'cardio', name, targetMinutes };
  }
  return {
    entryId,
    kind: 'strength',
    name,
    setTargets: ensureStrengthSetTargets(o),
  };
}

function coerceDay(raw: unknown, index: number): TrainingDay {
  if (!raw || typeof raw !== 'object') {
    return { dayId: newId(), title: `Day ${index + 1}`, exercises: [] };
  }
  const o = raw as Record<string, unknown>;
  const dayId = typeof o.dayId === 'string' && o.dayId.length > 0 ? o.dayId : newId();
  const title =
    typeof o.title === 'string' && o.title.trim() ? o.title.trim() : `Day ${index + 1}`;
  const exList = Array.isArray(o.exercises) ? o.exercises : [];
  const exercises = exList
    .map((e) => coerceExercise(e))
    .filter((x): x is TrainingExerciseEntry => x != null);
  return { dayId, title, exercises };
}

export function parsePlanExercises(raw: unknown): TrainingPlanPayload {
  if (Array.isArray(raw)) {
    const legacy = raw as LegacyWorkoutExerciseEntry[];
    return {
      days: [
        {
          dayId: newId(),
          title: 'Day 1',
          exercises: legacy.map(legacyToExercise),
        },
      ],
    };
  }
  if (raw && typeof raw === 'object' && 'days' in raw) {
    const daysRaw = (raw as { days: unknown }).days;
    if (Array.isArray(daysRaw) && daysRaw.length > 0) {
      return { days: daysRaw.map((d, i) => coerceDay(d, i)) };
    }
  }
  return {
    days: [{ dayId: newId(), title: 'Day 1', exercises: [] }],
  };
}

export function serializePlanPayload(payload: TrainingPlanPayload): { days: TrainingDay[] } {
  return { days: payload.days };
}

export function emptyTrainingPlanPayload(): TrainingPlanPayload {
  return { days: [{ dayId: newId(), title: 'Day 1', exercises: [] }] };
}
